import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Calendar,
  Plus,
  Upload,
  FileText,
  Clock,
  Check,
  X,
  ExternalLink,
  Download,
  Link2,
  LogOut,
} from "lucide-react";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const estadoColor = (estado) => {
  const m = {
    confirmada: "bg-green-100 text-green-800 border-green-300",
    pendiente: "bg-yellow-100 text-yellow-800 border-yellow-300",
    cancelada: "bg-red-100 text-red-800 border-red-300",
    completada: "bg-blue-100 text-blue-800 border-blue-300",
  };
  return m[estado] || m.pendiente;
};

const estadoIcon = (estado) => {
  const m = {
    confirmada: <Check className="w-4 h-4" />,
    pendiente: <Clock className="w-4 h-4" />,
    cancelada: <X className="w-4 h-4" />,
    completada: <Check className="w-4 h-4" />,
  };
  return m[estado];
};

const emptyForm = {
  titulo: "",
  fecha: "",
  hora: "",
  cliente: "",
  email_cliente: "",
  proyecto: "",
  direccion: "",
  personal: [],
  descripcion: "",
  estado: "pendiente",
  fecha_fin_proyecto: "",
};

export default function App() {
  const [activeTab, setActiveTab] = useState("calendar");
  const [citas, setCitas] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [proyectosMeta, setProyectosMeta] = useState([]);
  const [remindersStatus, setRemindersStatus] = useState(null);
  const [remindersLog, setRemindersLog] = useState([]);
  const [googleStatus, setGoogleStatus] = useState({
    configured: false,
    connected: false,
    email: null,
  });
  const [loading, setLoading] = useState(true);

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [citaForm, setCitaForm] = useState(emptyForm);
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [editando, setEditando] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm);
  const [nuevoPersonal, setNuevoPersonal] = useState({ nombre: "", especialidad: "", email: "" });
  const [editPersonalId, setEditPersonalId] = useState(null);
  const [editPersonalForm, setEditPersonalForm] = useState({ nombre: "", especialidad: "", email: "" });
  const [editProyecto, setEditProyecto] = useState(null); // {cliente, old_name, new_name}
  const [toast, setToast] = useState(null);
  const [filtros, setFiltros] = useState({
    estado: "todas",
    cliente: "",
    fechaDesde: "",
    fechaHasta: "",
  });

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = async () => {
    try {
      const [c, p, g, pm, rs, rl] = await Promise.all([
        axios.get(`${API}/citas`),
        axios.get(`${API}/personal`),
        axios.get(`${API}/oauth/calendar/status`),
        axios.get(`${API}/proyectos/meta`),
        axios.get(`${API}/reminders/status`),
        axios.get(`${API}/reminders/log`),
      ]);
      setCitas(c.data);
      setPersonal(p.data);
      setGoogleStatus(g.data);
      setProyectosMeta(pm.data);
      setRemindersStatus(rs.data);
      setRemindersLog(rl.data);
    } catch (e) {
      console.error(e);
      showToast("Error cargando datos", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEjecutarRecordatoriosAhora = async () => {
    if (!window.confirm("Esto enviará los recordatorios para proyectos que terminen en 3 o 1 días (o los que ya estén configurados). ¿Continuar?")) return;
    try {
      const { data } = await axios.post(`${API}/reminders/run-now`);
      showToast(`Enviados ${data.sent} recordatorio(s)`);
      const rl = await axios.get(`${API}/reminders/log`);
      setRemindersLog(rl.data);
    } catch (e) {
      showToast("Error ejecutando recordatorios", "error");
    }
  };

  useEffect(() => {
    loadAll();
    // Detect OAuth redirect back
    const url = new URL(window.location.href);
    if (url.searchParams.get("google") === "connected") {
      showToast("Google Calendar conectado correctamente");
      url.searchParams.delete("google");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, []);

  const citasFiltradas = useMemo(() => {
    return citas
      .filter((c) => {
        if (filtros.estado !== "todas" && c.estado !== filtros.estado) return false;
        if (filtros.cliente && !c.cliente.toLowerCase().includes(filtros.cliente.toLowerCase())) return false;
        if (filtros.fechaDesde && c.fecha < filtros.fechaDesde) return false;
        if (filtros.fechaHasta && c.fecha > filtros.fechaHasta) return false;
        return true;
      })
      .sort((a, b) => new Date(`${a.fecha}T${a.hora}`) - new Date(`${b.fecha}T${b.hora}`));
  }, [citas, filtros]);

  const citasPorFecha = useMemo(() => {
    return citasFiltradas.reduce((acc, c) => {
      (acc[c.fecha] = acc[c.fecha] || []).push(c);
      return acc;
    }, {});
  }, [citasFiltradas]);

  // ---- Actions ----
  const handleTogglePersonal = (nombre) => {
    setCitaForm((prev) => ({
      ...prev,
      personal: prev.personal.includes(nombre)
        ? prev.personal.filter((p) => p !== nombre)
        : [...prev.personal, nombre],
    }));
  };

  const handleToggleEditPersonal = (nombre) => {
    setEditForm((prev) => ({
      ...prev,
      personal: prev.personal.includes(nombre)
        ? prev.personal.filter((p) => p !== nombre)
        : [...prev.personal, nombre],
    }));
  };

  const handleStartEdit = () => {
    if (!citaSeleccionada) return;
    setEditForm({
      titulo: citaSeleccionada.titulo || "",
      fecha: citaSeleccionada.fecha || "",
      hora: citaSeleccionada.hora || "",
      cliente: citaSeleccionada.cliente || "",
      email_cliente: citaSeleccionada.email_cliente || "",
      proyecto: citaSeleccionada.proyecto || "",
      direccion: citaSeleccionada.direccion || "",
      personal: citaSeleccionada.personal || [],
      descripcion: citaSeleccionada.descripcion || "",
      estado: citaSeleccionada.estado || "pendiente",
      fecha_fin_proyecto: getProyectoFechaFin(
        citaSeleccionada.cliente,
        citaSeleccionada.proyecto
      ),
    });
    setEditando(true);
  };

  const handleGuardarEdicion = async () => {
    if (!editForm.titulo || !editForm.fecha || !editForm.hora || !editForm.cliente) {
      showToast("Completa los campos obligatorios", "error");
      return;
    }
    try {
      const { fecha_fin_proyecto, ...citaPayload } = editForm;
      const { data } = await axios.put(`${API}/citas/${citaSeleccionada.id}`, citaPayload);
      if (editForm.cliente && editForm.proyecto) {
        await handleGuardarFechaFin(
          editForm.cliente,
          editForm.proyecto,
          fecha_fin_proyecto || ""
        );
      }
      setCitas((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      setCitaSeleccionada(data);
      setEditando(false);
      showToast("Cita actualizada");
    } catch (e) {
      console.error(e);
      showToast("Error actualizando cita", "error");
    }
  };

  const handleCrearPersonal = async () => {
    if (!nuevoPersonal.nombre || !nuevoPersonal.especialidad) {
      showToast("Nombre y especialidad son obligatorios", "error");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/personal`, nuevoPersonal);
      setPersonal((prev) => [...prev, data]);
      setNuevoPersonal({ nombre: "", especialidad: "" });
      showToast("Técnico añadido");
    } catch (e) {
      showToast("Error creando personal", "error");
    }
  };

  const handleEliminarPersonal = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar a ${nombre}?`)) return;
    try {
      await axios.delete(`${API}/personal/${id}`);
      setPersonal((prev) => prev.filter((p) => p.id !== id));
      showToast("Técnico eliminado");
    } catch (e) {
      showToast("Error eliminando", "error");
    }
  };

  const handleStartEditPersonal = (p) => {
    setEditPersonalId(p.id);
    setEditPersonalForm({
      nombre: p.nombre || "",
      especialidad: p.especialidad || "",
      email: p.email || "",
    });
  };

  const handleGuardarEditPersonal = async () => {
    if (!editPersonalForm.nombre || !editPersonalForm.especialidad) {
      showToast("Nombre y especialidad obligatorios", "error");
      return;
    }
    try {
      const { data } = await axios.put(
        `${API}/personal/${editPersonalId}`,
        editPersonalForm
      );
      setPersonal((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      // Also update citas where this name was used
      await loadAll();
      setEditPersonalId(null);
      showToast("Personal actualizado");
    } catch (e) {
      showToast("Error actualizando", "error");
    }
  };

  const handleStartEditProyecto = (cliente, old_name) => {
    setEditProyecto({ cliente, old_name, new_name: old_name });
  };

  const handleGuardarEditProyecto = async () => {
    if (!editProyecto?.new_name?.trim()) {
      showToast("El nuevo nombre no puede estar vacío", "error");
      return;
    }
    try {
      await axios.put(`${API}/proyectos/rename`, editProyecto);
      await loadAll();
      setEditProyecto(null);
      showToast("Proyecto renombrado");
    } catch (e) {
      showToast("Error renombrando proyecto", "error");
    }
  };

  const getProyectoFechaFin = (cliente, nombre) => {
    const m = proyectosMeta.find((x) => x.cliente === cliente && x.nombre === nombre);
    return m?.fecha_fin_estimada || "";
  };

  const handleGuardarFechaFin = async (cliente, nombre, fecha_fin_estimada) => {
    try {
      await axios.put(`${API}/proyectos/meta`, {
        cliente,
        nombre,
        fecha_fin_estimada,
      });
      setProyectosMeta((prev) => {
        const idx = prev.findIndex(
          (x) => x.cliente === cliente && x.nombre === nombre
        );
        const next = { cliente, nombre, fecha_fin_estimada };
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = next;
          return copy;
        }
        return [...prev, next];
      });
      showToast("Fecha de fin guardada");
    } catch (e) {
      showToast("Error guardando fecha", "error");
    }
  };

  const handleCrearCita = async () => {
    if (!citaForm.titulo || !citaForm.fecha || !citaForm.hora || !citaForm.cliente) {
      showToast("Completa los campos obligatorios", "error");
      return;
    }
    try {
      // Separate the project-level metadata from the cita payload
      const { fecha_fin_proyecto, ...citaPayload } = citaForm;
      const { data } = await axios.post(`${API}/citas`, citaPayload);
      // If project + end date were filled, persist the project meta
      if (citaForm.cliente && citaForm.proyecto && fecha_fin_proyecto !== undefined) {
        await handleGuardarFechaFin(
          citaForm.cliente,
          citaForm.proyecto,
          fecha_fin_proyecto
        );
      }
      setCitas((prev) => [...prev, data]);
      setCitaForm(emptyForm);
      setMostrarFormulario(false);
      showToast("Cita creada");
    } catch (e) {
      console.error(e);
      showToast("Error creando cita", "error");
    }
  };

  const handleEliminarCita = async (id) => {
    if (!window.confirm("¿Deseas eliminar esta cita?")) return;
    try {
      await axios.delete(`${API}/citas/${id}`);
      setCitas((prev) => prev.filter((c) => c.id !== id));
      setCitaSeleccionada(null);
      showToast("Cita eliminada");
    } catch (e) {
      showToast("Error eliminando", "error");
    }
  };

  const handleActualizarEstado = async (id, estado) => {
    try {
      const { data } = await axios.put(`${API}/citas/${id}`, { estado });
      setCitas((prev) => prev.map((c) => (c.id === id ? data : c)));
      if (citaSeleccionada && citaSeleccionada.id === id) setCitaSeleccionada(data);
    } catch (e) {
      showToast("Error actualizando", "error");
    }
  };

  const handleUploadFile = async (citaId, fileList) => {
    const files = Array.from(fileList);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await axios.post(`${API}/citas/${citaId}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setCitas((prev) => prev.map((c) => (c.id === citaId ? data : c)));
        if (citaSeleccionada && citaSeleccionada.id === citaId) setCitaSeleccionada(data);
      } catch (e) {
        showToast(`Error subiendo ${file.name}`, "error");
      }
    }
    showToast("Archivos subidos");
  };

  const handleEliminarArchivo = async (citaId, filename) => {
    try {
      const { data } = await axios.delete(`${API}/citas/${citaId}/files/${filename}`);
      setCitas((prev) => prev.map((c) => (c.id === citaId ? data : c)));
      if (citaSeleccionada && citaSeleccionada.id === citaId) setCitaSeleccionada(data);
    } catch (e) {
      showToast("Error eliminando archivo", "error");
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const { data } = await axios.get(`${API}/oauth/calendar/login`);
      window.location.href = data.authorization_url;
    } catch (e) {
      showToast(
        e.response?.data?.detail || "Google Calendar no configurado (falta CLIENT_ID/SECRET)",
        "error"
      );
    }
  };

  const handleDisconnectGoogle = async () => {
    await axios.post(`${API}/oauth/calendar/disconnect`);
    setGoogleStatus({ ...googleStatus, connected: false, email: null });
    showToast("Google Calendar desconectado");
  };

  const handleSincronizarGoogle = async (cita) => {
    if (!googleStatus.connected) {
      showToast("Primero conecta Google Calendar", "error");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/citas/${cita.id}/sync-google`);
      setCitas((prev) => prev.map((c) => (c.id === cita.id ? data : c)));
      if (citaSeleccionada && citaSeleccionada.id === cita.id) setCitaSeleccionada(data);
      showToast(`"${data.titulo}" sincronizada con Google Calendar`);
    } catch (e) {
      showToast(e.response?.data?.detail || "Error sincronizando", "error");
    }
  };

  // ---- Render ----
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-testid="app-root">
      {/* Header */}
      <div className="bg-white shadow-sm border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">C</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="app-title">CONVITEL</h1>
              <p className="text-xs text-gray-600">Gestión de Citas y Servicios SAT</p>
            </div>
          </div>
          <div className="text-right text-sm">
            {googleStatus.connected ? (
              <div className="flex items-center gap-2">
                <span className="text-green-700 font-semibold" data-testid="google-status-connected">
                  ✅ {googleStatus.email}
                </span>
                <button
                  onClick={handleDisconnectGoogle}
                  data-testid="google-disconnect-btn"
                  className="text-xs text-gray-600 hover:text-red-600 flex items-center gap-1"
                  title="Desconectar"
                >
                  <LogOut className="w-3 h-3" /> Desconectar
                </button>
              </div>
            ) : (
              <button
                onClick={handleConnectGoogle}
                data-testid="google-connect-btn"
                className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm"
              >
                <Link2 className="w-4 h-4" />
                {googleStatus.configured
                  ? "Conectar con Google Calendar"
                  : "Google Calendar (no configurado)"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-8">
            {[
              { id: "calendar", label: "📅 Calendario" },
              { id: "lista", label: "📋 Lista de Citas" },
              { id: "proyectos", label: "🏗️ Proyectos" },
              { id: "personal", label: "👥 Personal" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`py-4 px-2 font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading && (
          <p className="text-gray-600" data-testid="loading-indicator">Cargando…</p>
        )}

        {/* Calendario */}
        {activeTab === "calendar" && !loading && (
          <div className="space-y-4" data-testid="view-calendar">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Calendario de Citas</h2>
              <button
                onClick={() => setMostrarFormulario((v) => !v)}
                data-testid="new-cita-btn"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-all shadow-md"
              >
                <Plus className="w-5 h-5" /> Nueva Cita
              </button>
            </div>

            {mostrarFormulario && (
              <div
                className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-600 mb-6"
                data-testid="new-cita-form"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">Nueva Cita SAT</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Título de la cita *"
                    value={citaForm.titulo}
                    onChange={(e) => setCitaForm({ ...citaForm, titulo: e.target.value })}
                    data-testid="input-titulo"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Cliente *"
                    value={citaForm.cliente}
                    onChange={(e) => setCitaForm({ ...citaForm, cliente: e.target.value })}
                    data-testid="input-cliente"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="email"
                    placeholder="Email del cliente (para recordatorios)"
                    value={citaForm.email_cliente}
                    onChange={(e) =>
                      setCitaForm({ ...citaForm, email_cliente: e.target.value })
                    }
                    data-testid="input-email-cliente"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="date"
                    value={citaForm.fecha}
                    onChange={(e) => setCitaForm({ ...citaForm, fecha: e.target.value })}
                    data-testid="input-fecha"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="time"
                    value={citaForm.hora}
                    onChange={(e) => setCitaForm({ ...citaForm, hora: e.target.value })}
                    data-testid="input-hora"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Proyecto"
                    value={citaForm.proyecto}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCitaForm({
                        ...citaForm,
                        proyecto: val,
                        fecha_fin_proyecto:
                          getProyectoFechaFin(citaForm.cliente, val) ||
                          citaForm.fecha_fin_proyecto,
                      });
                    }}
                    data-testid="input-proyecto"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={citaForm.estado}
                    onChange={(e) => setCitaForm({ ...citaForm, estado: e.target.value })}
                    data-testid="input-estado"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="confirmada">Confirmada</option>
                    <option value="completada">Completada</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                </div>

                <input
                  type="text"
                  placeholder="Dirección del proyecto (ej: Calle Mayor 10, Madrid) — se mostrará en Google Maps"
                  value={citaForm.direccion}
                  onChange={(e) => setCitaForm({ ...citaForm, direccion: e.target.value })}
                  data-testid="input-direccion"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                />

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    📅 Fecha estimada de fin del proyecto
                  </label>
                  <input
                    type="date"
                    value={citaForm.fecha_fin_proyecto}
                    onChange={(e) =>
                      setCitaForm({ ...citaForm, fecha_fin_proyecto: e.target.value })
                    }
                    data-testid="input-fecha-fin-proyecto"
                    className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Se vincula al proyecto y aparece también en la pestaña Proyectos.
                  </p>
                </div>

                <textarea
                  placeholder="Descripción"
                  value={citaForm.descripcion}
                  onChange={(e) => setCitaForm({ ...citaForm, descripcion: e.target.value })}
                  data-testid="input-descripcion"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                  rows="3"
                />

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Personal Asignado
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {personal.map((p) => (
                      <label
                        key={p.id}
                        className="flex items-center gap-2 p-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-blue-50"
                      >
                        <input
                          type="checkbox"
                          checked={citaForm.personal.includes(p.nombre)}
                          onChange={() => handleTogglePersonal(p.nombre)}
                          data-testid={`checkbox-personal-${p.nombre.replace(/\s+/g, "-")}`}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">
                          <div className="font-medium text-gray-900">{p.nombre}</div>
                          <div className="text-xs text-gray-600">{p.especialidad}</div>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleCrearCita}
                    data-testid="submit-cita-btn"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors"
                  >
                    ✅ Crear Cita
                  </button>
                  <button
                    onClick={() => setMostrarFormulario(false)}
                    data-testid="cancel-cita-btn"
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4" data-testid="calendar-groups">
              {Object.keys(citasPorFecha)
                .sort()
                .map((fecha) => (
                  <div
                    key={fecha}
                    className="bg-white rounded-xl shadow-md overflow-hidden border-l-4 border-blue-500"
                  >
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-3 border-b border-blue-200">
                      <h3 className="font-bold text-lg text-gray-900">
                        📅{" "}
                        {new Date(fecha + "T00:00").toLocaleDateString("es-ES", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </h3>
                    </div>
                    <div className="divide-y">
                      {citasPorFecha[fecha].map((cita) => (
                        <div
                          key={cita.id}
                          onClick={() => setCitaSeleccionada(cita)}
                          data-testid={`cita-card-${cita.id}`}
                          className="p-4 hover:bg-gray-50 cursor-pointer transition-colors border-l-4 border-orange-400"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1 flex-wrap">
                                <span className="font-bold text-blue-600 text-lg">{cita.hora}</span>
                                <h4 className="font-bold text-gray-900">{cita.titulo}</h4>
                                <span
                                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${estadoColor(
                                    cita.estado
                                  )}`}
                                >
                                  {estadoIcon(cita.estado)}
                                  {cita.estado.charAt(0).toUpperCase() + cita.estado.slice(1)}
                                </span>
                                {cita.proyecto &&
                                  getProyectoFechaFin(cita.cliente, cita.proyecto) && (() => {
                                    const fin = getProyectoFechaFin(cita.cliente, cita.proyecto);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const finDate = new Date(fin + "T00:00");
                                    const diff = Math.round(
                                      (finDate - today) / (1000 * 60 * 60 * 24)
                                    );
                                    let cls = "bg-green-100 text-green-800 border-green-300";
                                    let tail = `${diff}d`;
                                    if (diff < 0) {
                                      cls = "bg-red-100 text-red-800 border-red-300";
                                      tail = "vencido";
                                    } else if (diff <= 7) {
                                      cls = "bg-orange-100 text-orange-800 border-orange-300";
                                    }
                                    return (
                                      <span
                                        data-testid={`cita-fecha-fin-${cita.id}`}
                                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${cls}`}
                                        title={`Fin estimado del proyecto: ${fin}`}
                                      >
                                        📅 Fin: {fin} · {tail}
                                      </span>
                                    );
                                  })()}
                                {cita.google_event_id && (
                                  <span className="text-xs text-green-700 font-semibold">
                                    ✓ Google Calendar
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-700 font-medium">👤 {cita.cliente}</p>
                              {cita.proyecto && (
                                <p className="text-sm text-gray-600">🏗️ {cita.proyecto}</p>
                              )}
                              {cita.direccion && (
                                <p className="text-sm text-gray-600">
                                  📍{" "}
                                  <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                      cita.direccion
                                    )}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-blue-600 hover:text-blue-800 underline"
                                  >
                                    {cita.direccion}
                                  </a>
                                </p>
                              )}
                              {cita.personal?.length > 0 && (
                                <p className="text-sm text-gray-600">
                                  👥 {cita.personal.join(", ")}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSincronizarGoogle(cita);
                              }}
                              data-testid={`sync-btn-${cita.id}`}
                              className="ml-2 p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                              title="Sincronizar con Google Calendar"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              {Object.keys(citasPorFecha).length === 0 && (
                <p className="text-gray-600" data-testid="no-citas">No hay citas para mostrar.</p>
              )}
            </div>
          </div>
        )}

        {/* Lista */}
        {activeTab === "lista" && !loading && (
          <div className="space-y-4" data-testid="view-lista">
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h3 className="font-bold text-lg text-gray-900 mb-4">🔍 Filtros</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={filtros.estado}
                    onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                    data-testid="filter-estado"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="todas">Todas</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="confirmada">Confirmada</option>
                    <option value="completada">Completada</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                  <input
                    type="text"
                    placeholder="Buscar cliente..."
                    value={filtros.cliente}
                    onChange={(e) => setFiltros({ ...filtros, cliente: e.target.value })}
                    data-testid="filter-cliente"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
                  <input
                    type="date"
                    value={filtros.fechaDesde}
                    onChange={(e) => setFiltros({ ...filtros, fechaDesde: e.target.value })}
                    data-testid="filter-desde"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
                  <input
                    type="date"
                    value={filtros.fechaHasta}
                    onChange={(e) => setFiltros({ ...filtros, fechaHasta: e.target.value })}
                    data-testid="filter-hasta"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4" data-testid="lista-citas">
              {citasFiltradas.map((cita) => (
                <div
                  key={cita.id}
                  className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setCitaSeleccionada(cita)}
                  data-testid={`lista-cita-${cita.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">{cita.titulo}</h3>
                      <p className="text-sm text-gray-600">
                        📅 {cita.fecha} • ⏰ {cita.hora}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${estadoColor(
                        cita.estado
                      )}`}
                    >
                      {estadoIcon(cita.estado)}
                      {cita.estado.charAt(0).toUpperCase() + cita.estado.slice(1)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                    <div>
                      <p className="text-gray-600">👤 Cliente:</p>
                      <p className="font-medium text-gray-900">{cita.cliente}</p>
                    </div>
                    {cita.proyecto && (
                      <div>
                        <p className="text-gray-600">🏗️ Proyecto:</p>
                        <p className="font-medium text-gray-900">{cita.proyecto}</p>
                      </div>
                    )}
                    {cita.personal?.length > 0 && (
                      <div>
                        <p className="text-gray-600">👥 Personal:</p>
                        <p className="font-medium text-gray-900">{cita.personal.join(", ")}</p>
                      </div>
                    )}
                  </div>
                  {cita.descripcion && (
                    <p className="text-sm text-gray-700 mb-3 p-2 bg-gray-50 rounded">
                      {cita.descripcion}
                    </p>
                  )}
                  {cita.archivos?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">📎 Archivos:</p>
                      <div className="flex flex-wrap gap-2">
                        {cita.archivos.map((a) => (
                          <span
                            key={a.filename}
                            className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded"
                          >
                            {a.original_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {citasFiltradas.length === 0 && (
                <p className="text-gray-600">No hay citas que coincidan con los filtros.</p>
              )}
            </div>
          </div>
        )}

        {/* Proyectos */}
        {activeTab === "proyectos" && !loading && (
          <div className="space-y-4" data-testid="view-proyectos">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <h2 className="text-2xl font-bold text-gray-900">Proyectos por Cliente</h2>
              {remindersStatus?.smtp_configured && (
                <div className="bg-white border border-blue-200 rounded-lg p-3 shadow-sm text-sm">
                  <p className="font-semibold text-gray-900">
                    📧 Recordatorios automáticos activos
                  </p>
                  <p className="text-xs text-gray-600">
                    Se envían a las <strong>{remindersStatus.scheduled_time}</strong> cuando
                    faltan <strong>{remindersStatus.days_before.join(" o ")} día(s)</strong> para
                    el fin estimado de un proyecto.
                  </p>
                  <button
                    onClick={handleEjecutarRecordatoriosAhora}
                    data-testid="run-reminders-now-btn"
                    className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-1 rounded"
                  >
                    ▶ Ejecutar ahora
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from(new Set(citas.map((c) => c.cliente))).map((cliente) => {
                const citasCliente = citas.filter((c) => c.cliente === cliente);
                const proyectos = Array.from(
                  new Set(citasCliente.map((c) => c.proyecto).filter(Boolean))
                );
                return (
                  <div
                    key={cliente}
                    className="bg-white rounded-xl shadow-md p-6 border-t-4 border-blue-600"
                    data-testid={`proyecto-cliente-${cliente}`}
                  >
                    <h3 className="font-bold text-lg text-gray-900 mb-3">👤 {cliente}</h3>
                    <div className="space-y-2 mb-4">
                      {proyectos.map((proy) => {
                        const isEditing =
                          editProyecto &&
                          editProyecto.cliente === cliente &&
                          editProyecto.old_name === proy;
                        const fechaFin = getProyectoFechaFin(cliente, proy);
                        return (
                          <div
                            key={proy}
                            className="p-3 bg-blue-50 rounded border-l-4 border-blue-500 space-y-2"
                          >
                            {isEditing ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editProyecto.new_name}
                                  onChange={(e) =>
                                    setEditProyecto({
                                      ...editProyecto,
                                      new_name: e.target.value,
                                    })
                                  }
                                  data-testid={`edit-proyecto-input-${proy}`}
                                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                  onClick={handleGuardarEditProyecto}
                                  data-testid={`save-proyecto-${proy}`}
                                  className="text-xs bg-blue-600 hover:bg-blue-700 text-white font-medium px-2 py-1 rounded"
                                >
                                  Guardar
                                </button>
                                <button
                                  onClick={() => setEditProyecto(null)}
                                  className="text-xs bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium px-2 py-1 rounded"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-gray-900 text-sm">🏗️ {proy}</p>
                                  <p className="text-xs text-gray-600">
                                    {citasCliente.filter((c) => c.proyecto === proy).length}{" "}
                                    cita(s)
                                  </p>
                                </div>
                                <button
                                  onClick={() => handleStartEditProyecto(cliente, proy)}
                                  data-testid={`edit-proyecto-${proy}`}
                                  className="text-blue-600 hover:bg-blue-100 p-1 rounded"
                                  title="Renombrar proyecto"
                                >
                                  ✏️
                                </button>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-700 font-medium whitespace-nowrap">
                                📅 Fin estimado:
                              </label>
                              <input
                                type="date"
                                value={fechaFin}
                                onChange={(e) =>
                                  handleGuardarFechaFin(cliente, proy, e.target.value)
                                }
                                data-testid={`fecha-fin-${cliente}-${proy}`}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                              />
                              {fechaFin && (() => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const fin = new Date(fechaFin + "T00:00");
                                const diff = Math.round(
                                  (fin - today) / (1000 * 60 * 60 * 24)
                                );
                                if (diff < 0) {
                                  return (
                                    <span className="text-xs font-bold text-red-600 whitespace-nowrap">
                                      vencido
                                    </span>
                                  );
                                }
                                if (diff <= 7) {
                                  return (
                                    <span className="text-xs font-bold text-orange-600 whitespace-nowrap">
                                      {diff}d
                                    </span>
                                  );
                                }
                                return (
                                  <span className="text-xs font-medium text-green-700 whitespace-nowrap">
                                    {diff}d
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                      {proyectos.length === 0 && (
                        <p className="text-xs text-gray-500">Sin proyectos definidos</p>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">
                      <strong>{citasCliente.length}</strong> cita(s) total
                    </p>
                  </div>
                );
              })}
              {citas.length === 0 && (
                <p className="text-gray-600">No hay clientes registrados.</p>
              )}
            </div>

            {remindersStatus?.smtp_configured && (
              <div
                className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500 mt-6"
                data-testid="reminders-log"
              >
                <h3 className="font-bold text-lg text-gray-900 mb-3">
                  📧 Histórico de recordatorios enviados
                </h3>
                {remindersLog.length === 0 ? (
                  <p className="text-sm text-gray-600">
                    Aún no se ha enviado ningún recordatorio. Cuando falten 3 o 1 día para el
                    fin de un proyecto y haya emails registrados, se enviarán automáticamente
                    a las {remindersStatus.scheduled_time}.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-600 border-b">
                          <th className="py-2 pr-4">Enviado</th>
                          <th className="py-2 pr-4">Cliente</th>
                          <th className="py-2 pr-4">Proyecto</th>
                          <th className="py-2 pr-4">Fin</th>
                          <th className="py-2 pr-4">Aviso</th>
                          <th className="py-2 pr-4">Destinatarios</th>
                        </tr>
                      </thead>
                      <tbody>
                        {remindersLog.map((r, idx) => (
                          <tr
                            key={r.key || idx}
                            className="border-b last:border-0 hover:bg-gray-50"
                            data-testid={`reminder-row-${idx}`}
                          >
                            <td className="py-2 pr-4 text-xs text-gray-700">
                              {r.sent_at ? new Date(r.sent_at).toLocaleString("es-ES") : "—"}
                            </td>
                            <td className="py-2 pr-4 font-medium text-gray-900">{r.cliente}</td>
                            <td className="py-2 pr-4 text-gray-900">{r.proyecto}</td>
                            <td className="py-2 pr-4 text-gray-700">{r.fecha_fin}</td>
                            <td className="py-2 pr-4">
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                                {r.days_until === 1
                                  ? "1 día antes"
                                  : `${r.days_until} días antes`}
                              </span>
                            </td>
                            <td className="py-2 pr-4 text-xs text-gray-700">
                              {r.recipients && r.recipients.length > 0
                                ? r.recipients.join(", ")
                                : <em className="text-gray-400">sin destinatarios</em>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Personal */}
        {activeTab === "personal" && !loading && (
          <div className="space-y-4" data-testid="view-personal">
            <h2 className="text-2xl font-bold text-gray-900">Gestión de Personal</h2>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
              <h3 className="font-bold text-lg text-gray-900 mb-4">Añadir nuevo técnico</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <input
                  type="text"
                  placeholder="Nombre completo *"
                  value={nuevoPersonal.nombre}
                  onChange={(e) => setNuevoPersonal({ ...nuevoPersonal, nombre: e.target.value })}
                  data-testid="input-personal-nombre"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Especialidad *"
                  value={nuevoPersonal.especialidad}
                  onChange={(e) => setNuevoPersonal({ ...nuevoPersonal, especialidad: e.target.value })}
                  data-testid="input-personal-especialidad"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="email"
                  placeholder="Email (para invitación Google Calendar)"
                  value={nuevoPersonal.email}
                  onChange={(e) => setNuevoPersonal({ ...nuevoPersonal, email: e.target.value })}
                  data-testid="input-personal-email"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleCrearPersonal}
                  data-testid="submit-personal-btn"
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-all"
                >
                  <Plus className="w-5 h-5" /> Añadir
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Si añades un email al técnico, al sincronizar una cita con Google Calendar, el
                técnico recibirá la invitación por correo automáticamente.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {personal.map((p) => (
                <div
                  key={p.id}
                  data-testid={`personal-card-${p.id}`}
                  className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500"
                >
                  {editPersonalId === p.id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editPersonalForm.nombre}
                        onChange={(e) =>
                          setEditPersonalForm({ ...editPersonalForm, nombre: e.target.value })
                        }
                        data-testid={`edit-personal-nombre-${p.id}`}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nombre"
                      />
                      <input
                        type="text"
                        value={editPersonalForm.especialidad}
                        onChange={(e) =>
                          setEditPersonalForm({ ...editPersonalForm, especialidad: e.target.value })
                        }
                        data-testid={`edit-personal-especialidad-${p.id}`}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Especialidad"
                      />
                      <input
                        type="email"
                        value={editPersonalForm.email}
                        onChange={(e) =>
                          setEditPersonalForm({ ...editPersonalForm, email: e.target.value })
                        }
                        data-testid={`edit-personal-email-${p.id}`}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Email"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleGuardarEditPersonal}
                          data-testid={`save-personal-${p.id}`}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-1.5 rounded"
                        >
                          Guardar
                        </button>
                        <button
                          onClick={() => setEditPersonalId(null)}
                          data-testid={`cancel-personal-${p.id}`}
                          className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 text-sm font-medium py-1.5 rounded"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold text-gray-900">👤 {p.nombre}</p>
                        <p className="text-sm text-gray-600">{p.especialidad}</p>
                        {p.email ? (
                          <p className="text-xs text-blue-600 mt-1">✉️ {p.email}</p>
                        ) : (
                          <p className="text-xs text-gray-400 mt-1 italic">Sin email</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleStartEditPersonal(p)}
                          data-testid={`edit-personal-${p.id}`}
                          className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg transition-colors"
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleEliminarPersonal(p.id, p.nombre)}
                          data-testid={`delete-personal-${p.id}`}
                          className="text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                          title="Eliminar técnico"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {personal.length === 0 && (
                <p className="text-gray-600">No hay técnicos registrados.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal Detalle */}
      {citaSeleccionada && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          data-testid="cita-detail-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-t-4 border-blue-600">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">{citaSeleccionada.titulo}</h2>
                <p className="text-blue-100">
                  📅 {citaSeleccionada.fecha} • ⏰ {citaSeleccionada.hora}
                </p>
              </div>
              <button
                onClick={() => { setCitaSeleccionada(null); setEditando(false); }}
                data-testid="close-detail-btn"
                className="text-white hover:bg-blue-500 p-2 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {editando ? (
                <div className="space-y-4" data-testid="edit-cita-form">
                  <h3 className="text-lg font-bold text-gray-900">Editando cita</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Título *"
                      value={editForm.titulo}
                      onChange={(e) => setEditForm({ ...editForm, titulo: e.target.value })}
                      data-testid="edit-input-titulo"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Cliente *"
                      value={editForm.cliente}
                      onChange={(e) => setEditForm({ ...editForm, cliente: e.target.value })}
                      data-testid="edit-input-cliente"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="email"
                      placeholder="Email del cliente"
                      value={editForm.email_cliente}
                      onChange={(e) =>
                        setEditForm({ ...editForm, email_cliente: e.target.value })
                      }
                      data-testid="edit-input-email-cliente"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="date"
                      value={editForm.fecha}
                      onChange={(e) => setEditForm({ ...editForm, fecha: e.target.value })}
                      data-testid="edit-input-fecha"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="time"
                      value={editForm.hora}
                      onChange={(e) => setEditForm({ ...editForm, hora: e.target.value })}
                      data-testid="edit-input-hora"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Proyecto"
                      value={editForm.proyecto}
                      onChange={(e) => setEditForm({ ...editForm, proyecto: e.target.value })}
                      data-testid="edit-input-proyecto"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <select
                      value={editForm.estado}
                      onChange={(e) => setEditForm({ ...editForm, estado: e.target.value })}
                      data-testid="edit-input-estado"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pendiente">Pendiente</option>
                      <option value="confirmada">Confirmada</option>
                      <option value="completada">Completada</option>
                      <option value="cancelada">Cancelada</option>
                    </select>
                  </div>
                  <input
                    type="text"
                    placeholder="Dirección del proyecto (Google Maps)"
                    value={editForm.direccion}
                    onChange={(e) => setEditForm({ ...editForm, direccion: e.target.value })}
                    data-testid="edit-input-direccion"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      📅 Fecha estimada de fin del proyecto
                    </label>
                    <input
                      type="date"
                      value={editForm.fecha_fin_proyecto}
                      onChange={(e) =>
                        setEditForm({ ...editForm, fecha_fin_proyecto: e.target.value })
                      }
                      data-testid="edit-input-fecha-fin-proyecto"
                      className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <textarea
                    placeholder="Descripción"
                    value={editForm.descripcion}
                    onChange={(e) => setEditForm({ ...editForm, descripcion: e.target.value })}
                    data-testid="edit-input-descripcion"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Personal Asignado
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {personal.map((p) => (
                        <label
                          key={p.id}
                          className="flex items-center gap-2 p-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-blue-50"
                        >
                          <input
                            type="checkbox"
                            checked={editForm.personal.includes(p.nombre)}
                            onChange={() => handleToggleEditPersonal(p.nombre)}
                            data-testid={`edit-checkbox-personal-${p.nombre.replace(/\s+/g, "-")}`}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-sm">
                            <div className="font-medium text-gray-900">{p.nombre}</div>
                            <div className="text-xs text-gray-600">{p.especialidad}</div>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2 border-t">
                    <button
                      onClick={handleGuardarEdicion}
                      data-testid="save-edit-btn"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors"
                    >
                      💾 Guardar cambios
                    </button>
                    <button
                      onClick={() => setEditando(false)}
                      data-testid="cancel-edit-btn"
                      className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 rounded-lg transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600 text-sm">👤 Cliente</p>
                  <p className="font-bold text-gray-900">{citaSeleccionada.cliente}</p>
                  {citaSeleccionada.email_cliente && (
                    <p className="text-xs text-blue-600">✉️ {citaSeleccionada.email_cliente}</p>
                  )}
                </div>
                {citaSeleccionada.proyecto && (
                  <div>
                    <p className="text-gray-600 text-sm">🏗️ Proyecto</p>
                    <p className="font-bold text-gray-900">{citaSeleccionada.proyecto}</p>
                  </div>
                )}
              </div>

              {citaSeleccionada.direccion && (
                <div>
                  <p className="text-gray-600 text-sm">📍 Dirección</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      citaSeleccionada.direccion
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="maps-link"
                    className="font-bold text-blue-600 hover:text-blue-800 underline"
                  >
                    {citaSeleccionada.direccion}
                  </a>
                </div>
              )}

              {citaSeleccionada.proyecto &&
                getProyectoFechaFin(citaSeleccionada.cliente, citaSeleccionada.proyecto) && (
                  <div>
                    <p className="text-gray-600 text-sm">📅 Fin estimado del proyecto</p>
                    <p className="font-bold text-gray-900">
                      {getProyectoFechaFin(
                        citaSeleccionada.cliente,
                        citaSeleccionada.proyecto
                      )}
                    </p>
                  </div>
                )}

              <div className="flex gap-4 items-end">
                <div>
                  <p className="text-gray-600 text-sm">Estado</p>
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${estadoColor(
                      citaSeleccionada.estado
                    )}`}
                  >
                    {estadoIcon(citaSeleccionada.estado)}
                    {citaSeleccionada.estado.charAt(0).toUpperCase() +
                      citaSeleccionada.estado.slice(1)}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-gray-600 text-sm">Cambiar estado</p>
                  <select
                    value={citaSeleccionada.estado}
                    onChange={(e) => handleActualizarEstado(citaSeleccionada.id, e.target.value)}
                    data-testid="detail-estado-select"
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="confirmada">Confirmada</option>
                    <option value="completada">Completada</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                </div>
              </div>

              {citaSeleccionada.descripcion && (
                <div>
                  <p className="text-gray-600 text-sm font-semibold">Descripción</p>
                  <p className="text-gray-900">{citaSeleccionada.descripcion}</p>
                </div>
              )}

              {citaSeleccionada.personal?.length > 0 && (
                <div>
                  <p className="text-gray-600 text-sm font-semibold">👥 Personal Asignado</p>
                  <div className="flex flex-wrap gap-2">
                    {citaSeleccionada.personal.map((per, idx) => (
                      <span
                        key={idx}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {per}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-gray-600 text-sm font-semibold">📎 Archivos Adjuntos</p>
                  <label className="px-3 py-1 bg-orange-100 text-orange-700 rounded-lg cursor-pointer hover:bg-orange-200 transition-colors font-medium flex items-center gap-2 text-sm">
                    <Upload className="w-4 h-4" /> Subir
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleUploadFile(citaSeleccionada.id, e.target.files)}
                      data-testid="upload-file-input"
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="space-y-2">
                  {(citaSeleccionada.archivos || []).map((a) => (
                    <div
                      key={a.filename}
                      className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200"
                    >
                      <span className="text-gray-800 font-medium text-sm">📎 {a.original_name}</span>
                      <div className="flex items-center gap-2">
                        <a
                          href={`${API}/files/${a.filename}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`download-${a.filename}`}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" /> Descargar
                        </a>
                        <button
                          onClick={() => handleEliminarArchivo(citaSeleccionada.id, a.filename)}
                          data-testid={`delete-file-${a.filename}`}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!citaSeleccionada.archivos || citaSeleccionada.archivos.length === 0) && (
                    <p className="text-xs text-gray-500">Sin archivos adjuntos.</p>
                  )}
                </div>
              </div>

              {citaSeleccionada.google_event_id && (
                <p className="text-xs text-green-700 font-semibold" data-testid="google-event-synced">
                  ✓ Sincronizado en Google Calendar (event id: {citaSeleccionada.google_event_id})
                </p>
              )}

              <div className="flex gap-2 pt-4 border-t flex-wrap">
                <button
                  onClick={handleStartEdit}
                  data-testid="detail-edit-btn"
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 rounded-lg transition-colors min-w-[120px]"
                >
                  ✏️ Editar
                </button>
                <button
                  onClick={() => handleSincronizarGoogle(citaSeleccionada)}
                  data-testid="detail-sync-btn"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors min-w-[120px]"
                >
                  📅 Sincronizar Google
                </button>
                <button
                  onClick={() => handleEliminarCita(citaSeleccionada.id)}
                  data-testid="detail-delete-btn"
                  className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  🗑️
                </button>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          data-testid="toast"
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-white font-medium z-50 ${
            toast.type === "error" ? "bg-red-600" : "bg-green-600"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
